// Highlight colors for session cards/detail/filters when the guide is colored *by track* (the
// alternative to coloring by room — see roomColors.js). Which mode is active comes from the CMS
// color-coding setting (see colorCoding.js).
//
// Tracks are identified throughout the agenda data by their **name** (a session carries
// `event.tracks` as an array of track-name strings, and the tracks facet uses `track.name` as its
// value), so colors are keyed by track name rather than by id.
//
// Assignment precedence (see buildTrackColorMap / resolveTrackColor):
//   1. An explicit color on the track record — `track.color`. These will eventually come from the
//      API; this file is the interim source until they do.
//   2. A named override below — a fixed color for a specific, known track name.
//   3. The fallback palette — assigned in stable name order to any remaining tracks.

// Fixed colors for specific, known track names. Matched case-insensitively against `track.name`.
export const trackColorOverrides = {
    coronary: "#990000",
    // Hue 220 (was #336699 at hue 210), shifted 10° away from the innovation teal so the two
    // blues stay distinct at a glance. Same saturation/lightness as before; 7.2:1 on white.
    structural: "#335599",
    endovascular: "#009999",
    "heart failure": "#330033",
    // Same hue as the original #47d5fe (hsl 193°), darkened so white text on it clears WCAG AA
    // (4.7:1 on white, the lightest step that passes; the original was 1.7:1).
    innovation: "#017ea2",
}

// Colors for tracks with no explicit or overridden color, assigned in stable name order. Green and
// orange lead (so the first two unmatched tracks take them); the rest — a cohesive HSL set sharing
// one saturation/lightness — cover any additional tracks a conference might have.
const TRACK_S = 68 // % saturation, shared across the HSL entries
const TRACK_L = 50 // % lightness, shared across the HSL entries
export const trackFallbackPalette = [
    "#1E8A4C", // green — a darker "CME" green (deliberately deeper than the shared HSL set)
    `hsl(30, ${TRACK_S}%, ${TRACK_L}%)`, // orange
    `hsl(190, ${TRACK_S}%, ${TRACK_L}%)`, // cyan
    `hsl(275, ${TRACK_S}%, ${TRACK_L}%)`, // violet
    `hsl(320, ${TRACK_S}%, ${TRACK_L}%)`, // magenta
    `hsl(100, ${TRACK_S}%, ${TRACK_L}%)`, // lime
    `hsl(210, ${TRACK_S}%, ${TRACK_L}%)`, // blue
    `hsl(350, ${TRACK_S}%, ${TRACK_L}%)`, // red
]

export const defaultTrackColor = trackFallbackPalette[0]

// Color used for the track set when color-coding is NOT "byTrack" (i.e. "byRoom" or "none"): the
// neutral scaffolding gray, so track pills/tags read as plain (uncolored) labels. Kept separate
// from the palette so changing the palette can't shift the neutral appearance.
export const trackInactiveColor = "gray.500"

// Emma/CMS track colors sometimes arrive as bare hex ("F03E3E") without the leading "#", which
// Chakra silently renders as an invalid color. Prefix those; pass named tokens and rgb()/hsl()
// strings through untouched. Empty/whitespace/non-strings resolve to null.
function normalizeColor(value) {
    if (typeof value !== "string") return null
    const trimmed = value.trim()
    if (!trimmed) return null
    return /^[0-9a-f]{3,8}$/i.test(trimmed) ? `#${trimmed}` : trimmed
}

// Normalize a track name for override lookup: trimmed, lower-cased, inner whitespace collapsed, so
// "Heart Failure" / "heart failure" / "Heart  Failure" all match the same override key.
function normalizeName(name) {
    return String(name ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ")
}

// Small deterministic string hash so tracks missing from the color map still get a stable,
// distinct fallback color instead of all collapsing onto the default.
function hashToIndex(key, mod) {
    const str = String(key ?? "")
    let h = 0
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0
    return Math.abs(h) % mod
}

// Build a { [trackName]: color } map from the track records. Precedence per track: an explicit
// `track.color`, then a named override, then the fallback palette. Fallback colors are handed out
// in stable name order, and only counted for tracks that actually fall through to them — so the
// first two *unmatched* tracks get green and orange regardless of how many named tracks precede them.
export function buildTrackColorMap(tracks = []) {
    const map = {}
    let fallbackIndex = 0

    const sorted = [...tracks].sort((a, b) =>
        String(a.name).localeCompare(String(b.name), undefined, { numeric: true }),
    )
    sorted.forEach(track => {
        if (!track?.name) return
        const explicit = normalizeColor(track.color)
        const override = trackColorOverrides[normalizeName(track.name)]
        if (explicit || override) {
            map[track.name] = explicit || override
            return
        }
        map[track.name] = trackFallbackPalette[fallbackIndex % trackFallbackPalette.length]
        fallbackIndex++
    })

    return map
}

// Resolve the highlight color for a track. `track` may be a plain name string (as carried on a
// session's `event.tracks`) or a track record. Order: an explicit color on a track record, then the
// pre-built map, then a named override, then a deterministic hash fallback so tracks absent from the
// map still get a distinct, stable color.
export function resolveTrackColor(colorMap, track) {
    if (!track) return defaultTrackColor
    const name = typeof track === "string" ? track : track.name
    const explicit = typeof track === "object" ? normalizeColor(track.color) : null
    return (
        explicit ||
        colorMap?.[name] ||
        trackColorOverrides[normalizeName(name)] ||
        trackFallbackPalette[hashToIndex(name, trackFallbackPalette.length)]
    )
}
